import ControlPanelController from "./control_panel_controller"

/**
 * Stimulus controller for Currencies / Measurements CRUD.
 * Reused across localization sub-tabs via data-cpanel-currencies-resource-key-value.
 */
export default class extends ControlPanelController {
  static targets = [
    "createModal", "editModal", "deleteConfirmModal",
    "createName", "createSymbol", "createFormat",
    "editId", "editName", "editSymbol", "editFormat",
    "deleteId", "tableBody"
  ]

  static values = {
    createUrl: String,
    baseUrl: String,
    resourceKey: { type: String, default: "currency" }
  }

  // ── Create ────────────────────────────────────────────────────────

  submitCreate() {
    const body = {
      name: this.createNameTarget.value.trim(),
      symbol: this.createSymbolTarget.value.trim()
    }
    if (this.hasCreateFormatTarget) {
      body.format = this.createFormatTarget.value
    }
    const fields = [this.createNameTarget, this.createSymbolTarget]
    if (!this._validateRequired(fields)) return

    const payload = {}
    payload[this.resourceKeyValue] = body

    this.submitCreateRequest(this.createUrlValue, payload)
  }

  async submitCreateRequest(url, payload) {
    await this._submitCreate(url, payload)
  }

  async _submitCreate(url, payload) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeCreateModal()
        this._showToast(data.message || "Created successfully")
        this._reload()
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Edit ──────────────────────────────────────────────────────────

  openEditModal(event) {
    const btn = event.currentTarget
    this.editIdTarget.value = btn.dataset.id
    this.editNameTarget.value = btn.dataset.name || ""
    this.editSymbolTarget.value = btn.dataset.symbol || ""
    if (this.hasEditFormatTarget && btn.dataset.format) {
      this.editFormatTarget.value = btn.dataset.format
    }
    this.editModalTarget.classList.remove("hidden")
  }

  submitUpdate() {
    const id = this.editIdTarget.value
    const body = {
      name: this.editNameTarget.value.trim(),
      symbol: this.editSymbolTarget.value.trim()
    }
    if (this.hasEditFormatTarget) {
      body.format = this.editFormatTarget.value
    }
    const fields = [this.editNameTarget, this.editSymbolTarget]
    if (!this._validateRequired(fields)) return

    const payload = {}
    payload[this.resourceKeyValue] = body

    this._submitUpdate(`${this.baseUrlValue}/${id}`, payload)
  }

  async _submitUpdate(url, payload) {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditModal()
        this._showToast(data.message || "Updated successfully")
        this._reload()
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Delete ────────────────────────────────────────────────────────

  openDeleteConfirm(event) {
    this.deleteIdTarget.value = event.currentTarget.dataset.id
    this.deleteConfirmModalTarget.classList.remove("hidden")
  }

  closeDeleteConfirm() {
    this.deleteConfirmModalTarget.classList.add("hidden")
  }

  submitDelete() {
    const id = this.deleteIdTarget.value
    this._submitDelete(`${this.baseUrlValue}/${id}`)
  }

  _validateRequired(fields) {
    let valid = true
    fields.forEach(field => {
      if (!field.value.trim()) {
        field.classList.remove("border-gray-300")
        field.classList.add("border-red-500")
        valid = false
      }
    })
    return valid
  }

  _clearValidation(event) {
    const field = event.currentTarget
    if (field.value.trim()) {
      field.classList.remove("border-red-500")
      field.classList.add("border-gray-300")
    }
  }

  connect() {
    super.connect()
    const inputs = [
      ...( this.hasCreateNameTarget ? [this.createNameTarget] : []),
      ...(this.hasCreateSymbolTarget ? [this.createSymbolTarget] : []),
      ...(this.hasEditNameTarget ? [this.editNameTarget] : []),
      ...(this.hasEditSymbolTarget ? [this.editSymbolTarget] : [])
    ]
    inputs.forEach(input => {
      input.addEventListener("input", this._clearValidation.bind(this))
    })
  }

  _reload() {
    this._refreshTable()
  }

  async _refreshTable() {
    try {
      const response = await fetch(window.location.href, { headers: { "Accept": "text/html" } })
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newTbody = doc.querySelector("[data-cpanel-currencies-target='tableBody']")
      if (newTbody && this.hasTableBodyTarget) {
        this.tableBodyTarget.innerHTML = newTbody.innerHTML
      }
    } catch {
      window.location.reload()
    }
  }

  async _submitDelete(url) {
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Deleted successfully")
        this._reload()
      } else {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Cannot delete", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }
}
